import { useState, useEffect } from "react";
import { useLocation } from "@/lib/router-compat";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import { Send, MessageCircle } from "lucide-react";
import { publicSupabase } from "@/lib/public-supabase";
import { formatPriceDisplay } from '@/utils/currency';

const WhatsAppIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    role="img"
    aria-label="WhatsApp"
    {...props}
  >
    <path
      fill="currentColor"
      d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.472-.149-.672.149-.198.297-.768.967-.941 1.166-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.148-.173.198-.297.297-.495.099-.198.05-.371-.025-.52-.075-.149-.672-1.611-.921-2.206-.242-.579-.487-.5-.672-.51l-.572-.01c-.198 0-.52.074-.793.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.262.489 1.694.626.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.571-.347m-4.421 6.421h-.005a8.658 8.658 0 01-4.688-1.366l-.337-.2-3.48.912.929-3.392-.219-.348a8.682 8.682 0 01-1.337-4.637c.001-4.798 3.908-8.703 8.709-8.703 2.324 0 4.507.905 6.147 2.544a8.561 8.561 0 012.557 6.133c-.003 4.798-3.908 8.703-8.706 8.703M20.52 3.449A11.815 11.815 0 0012.047 0C5.495 0 .273 5.223.27 11.777c0 2.077.543 4.108 1.58 5.9L0 24l6.4-1.679a11.82 11.82 0 005.651 1.439h.005c6.554 0 11.777-5.223 11.78-11.777a11.72 11.72 0 00-3.316-8.534z"
    />
  </svg>
);

interface FloatingWhatsAppButtonProps {
  businessPhone?: string;
  businessName?: string;
}

export const FloatingWhatsAppButton = ({
  businessPhone = "+917021011304",
  businessName = "Mohnish Nagpal Real Estate"
}: FloatingWhatsAppButtonProps) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [propertyData, setPropertyData] = useState<any>(null);
  const location = useLocation();

  const currentPageUrl = typeof window === "undefined"
    ? `https://mnre.in${location.pathname}`
    : window.location.href;

  useEffect(() => {
    const fetchPropertyData = async () => {
      const pathMatch = location.pathname.match(/\/property\/(.+)/);
      if (pathMatch) {
        const propertyId = pathMatch[1];

        try {
          const { data, error } = await publicSupabase
            .from('properties')
            .select('title, price, locality, city')
            .eq('id', propertyId)
            .maybeSingle();

          if (data && !error) {
            const formattedLocation = `${data.locality || ''}${data.locality && data.city ? ', ' : ''}${data.city || ''}`;
            setPropertyData({ ...data, location: formattedLocation || 'Mumbai' });
          }
        } catch (error) {
          console.error('Error fetching property data:', error);
        }
      } else {
        setPropertyData(null);
      }
    };

    fetchPropertyData();
  }, [location.pathname]);

  const getWhatsAppMessage = () => {
    if (propertyData) {
      return `Hi ${businessName}! 👋

I'm interested in this property:

🏠 *${propertyData.title}*
💰 *Price: ${formatPriceDisplay({ price: propertyData.price })}*
📍 *Location: ${propertyData.location}*

🔗 Property Link: ${currentPageUrl}

Please share more details about this property. Looking forward to hearing from you!`;
    }

    return `Hi ${businessName}! 👋

I'm interested in your real estate services. I found your website and would like to know more about:

• Available properties
• Property investment opportunities
• Market insights

🔗 Current page: ${currentPageUrl}

Looking forward to hearing from you!`;
  };

  const openWhatsApp = (msg: string) => {
    const url = `https://wa.me/${businessPhone.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(msg)}`;
    window.location.href = url;
  };

  const handleQuickContact = () => {
    openWhatsApp(getWhatsAppMessage());
    toast({
      title: "WhatsApp Opened",
      description: propertyData ? "Property details shared with our expert" : "You can now chat with our property expert",
    });
  };

  const handleCustomMessage = () => {
    openWhatsApp(message || getWhatsAppMessage());
    toast({
      title: "WhatsApp Opened",
      description: "Your message is ready to send",
    });
    setOpen(false);
  };

  return (
    <>
      {/* Quick contact WhatsApp button - bottom of the stack */}
      <Button
        onClick={handleQuickContact}
        size="lg"
        className="fixed bottom-6 right-6 z-50 rounded-full w-14 h-14 bg-green-500 hover:bg-green-600 text-white shadow-lg hover:shadow-xl transition-all duration-300"
        aria-label="Contact us on WhatsApp"
      >
        <WhatsAppIcon className="h-6 w-6" />
      </Button>

      {/* Custom message dialog button - middle of the stack */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button
            size="lg"
            className="fixed bottom-[5.5rem] right-6 z-50 rounded-full w-14 h-14 bg-green-100 hover:bg-green-200 text-green-600 border border-green-200 shadow-lg hover:shadow-xl transition-all duration-300"
            aria-label="Send custom WhatsApp message"
          >
            <MessageCircle className="h-6 w-6" />
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <WhatsAppIcon className="h-5 w-5 text-green-600" />
              Contact Us on WhatsApp
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="contact-message">Your Message</Label>
              <Textarea
                id="contact-message"
                placeholder="Type your message here..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={6}
                className="text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Leave empty to use default greeting message
              </p>
            </div>

            <div className="flex space-x-2">
              <Button
                onClick={handleCustomMessage}
                className="flex-1"
              >
                <Send className="h-4 w-4 mr-2" />
                Send Message
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};